<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '../stores/user'
import { useIntervalFn } from '@vueuse/core'
import { apiSendSmsVerifyCode, apiRegisterWithPhone } from '../apis/user'
import { Toast } from 'vant'
import { SmsCodeType } from '@/types/apis/user'
import { LoaderCircle } from 'lucide-vue-next'

const router = useRouter()
const { t } = useI18n()
const route = useRoute()
const userStore = useUserStore()

const phone = ref('')
const code = ref('')
const errorMsg = ref('')
const countdown = ref(0)
const mode = ref<'login' | 'register'>('login')
const isSendingCode = ref(false)

const { pause: stopCountdown, resume: resumeCountdown } = useIntervalFn(
  () => {
    if (countdown.value <= 1) {
      countdown.value = 0
      stopCountdown()
      return
    }
    countdown.value -= 1
  },
  1000,
  { immediate: false },
)

// 获取redirect参数
const redirect = ref((route.query.redirect as string) || '')

const startCountdown = (seconds = 60) => {
  stopCountdown()
  countdown.value = seconds
  resumeCountdown()
}
onBeforeUnmount(() => stopCountdown())

const countdownText = computed(() =>
  countdown.value > 0
    ? t('login.form.getCodeCountdown', { countdown: countdown.value })
    : t('login.form.getCodeButton'),
)
const isCounting = computed(() => countdown.value > 0)

const openAgreementPage = (type: 'terms' | 'privacy') => {
  router.push(type === 'terms' ? '/terms-of-service' : '/privacy-policy')
}

const validatePhone = (phoneNum: string) => /^1[3-9]\d{9}$/.test(phoneNum)

const switchMode = () => {
  mode.value = mode.value === 'login' ? 'register' : 'login'
  code.value = ''
  errorMsg.value = ''
  stopCountdown()
}

const sendCode = async () => {
  if (isSendingCode.value || isCounting.value) return
  if (!validatePhone(phone.value)) {
    errorMsg.value = t('login.form.phoneError')
    return
  }

  errorMsg.value = ''
  isSendingCode.value = true

  try {
    await apiSendSmsVerifyCode({
      Phone: phone.value,
      TypeId: mode.value === 'login' ? SmsCodeType.Login : SmsCodeType.Register,
      AreaCode: '+86',
    })
    startCountdown()
  } catch (error) {
    Toast.fail(t('login.errors.sendCodeFailed'))
  } finally {
    isSendingCode.value = false
  }
}

// 提交表单（登录或注册）
const isLoading = ref(false)
const handleSubmit = async () => {
  if (isLoading.value) return

  if (!validatePhone(phone.value)) {
    errorMsg.value = t('login.form.phoneError')
    return
  }
  if (!code.value || code.value.length !== 4) {
    errorMsg.value = t('login.form.codeLengthError')
    return
  }

  errorMsg.value = ''
  isLoading.value = true

  try {
    switch (mode.value) {
      case 'login':
        await login()
        break
      case 'register':
        await register()
        break
      default:
        break
    }
  } finally {
    isLoading.value = false
  }
}

const login = async () => {
  await userStore.login({
    Phone: phone.value,
    VerifyCode: code.value,
    AreaCode: '+86',
  })

  if (redirect.value) {
    const redirectUrl = decodeURIComponent(redirect.value)
    const separator = redirectUrl.includes('?') ? '&' : '?'
    router.replace(`${redirectUrl}${separator}`)
  } else {
    router.replace({ path: '/'})
  }
}

const register = async () => {
  await apiRegisterWithPhone({
    Phone: phone.value,
    VerifyCode: code.value,
    AreaCode: '+86',
  })
  Toast.success(t('login.messages.registerSuccess'))
  switchMode()
  // 重置计时器
  stopCountdown()
  countdown.value = 0
}
</script>

<template>
  <div class="page-content min-h-screen flex flex-col items-center justify-center py-12 px-4">
    <div class="w-full max-w-md">
      <!-- 标题 -->
      <div class="text-center mb-12">
        <h1 class="text-3xl font-bold text-white mb-2">{{ t('login.page.title') }}</h1>
        <p class="text-gray-400">
          {{ t(`login.page.subtitle`) }}
        </p>
      </div>

      <!-- 登录表单 -->
      <div class="rounded-2xl shadow-xl p-8">
        <!-- 手机号输入 -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('login.form.phoneLabel') }}</label>
          <div class="relative">
            <input
              v-model="phone"
              type="tel"
              :placeholder="t('login.placeholders.phone')"
              class="w-full py-3 px-4 bg-gray-800 text-white rounded-lg"
              maxlength="11"
            />
          </div>
        </div>

        <!-- 验证码输入 -->
        <div class="mb-8 w-full">
          <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('login.form.codeLabel') }}</label>
          <div class="flex items-center py-3 bg-gray-800 text-white rounded-lg">
            <div class="flex-1 flex">
              <input
                v-model="code"
                @keyup.private.enter="handleSubmit"
                type="text"
                :placeholder="t('login.placeholders.code')"
                class="px-4 flex-1 w-full min-w-0! bg-gray-800 text-white rounded-lg"
                maxlength="6"
              />
            </div>
            <span class="h-3.5 bg-white/20 w-px"></span>
            <div
              @click="sendCode"
              class="text-nowrap text-sm w-1/3 text-center text-white"
              :class="{ 'opacity-60': isCounting || isSendingCode || !phone }"
            >
              {{ countdownText }}
            </div>
          </div>
        </div>

        <!-- 错误提示 -->
        <div v-if="errorMsg" class="text-red-500 text-sm mb-6">
          {{ errorMsg }}
        </div>

        <!-- 登录/注册按钮 -->
        <button
          class="h-12 bg-purple-600 w-full rounded-xl flex items-center justify-center gap-2"
          @click="handleSubmit"
          :disabled="isLoading"
        >
          <LoaderCircle class="animate-spin" v-if="isLoading" />
          {{ mode === 'login' ? t('login.form.loginButton') : t('login.form.registerButton') }}
        </button>

        <!-- 切换登录/注册模式 -->
        <div class="text-center mt-6">
          <span class="text-gray-400 text-sm">{{
            mode === 'login' ? t('login.form.switchToRegister') : t('login.form.switchToLogin')
          }}</span>
          <span @click="switchMode" class="text-purple-400 text-sm hover:text-purple-300 ml-1">{{
            mode === 'login' ? t('login.form.goRegister') : t('login.form.goLogin')
          }}</span>
        </div>

        <!-- 其他登录方式 -->
        <!--         <div class="mt-8 text-center">
          <p class="text-gray-500 text-sm mb-4">其他登录方式</p>
          <div class="flex justify-center gap-4">
            <button class="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors">
              <span class="text-xl"><Mail /></span>
            </button>
          </div>
        </div> -->
      </div>

      <!-- 底部提示 -->
      <div class="text-center mt-8 text-gray-500 text-sm">
        <p>
          {{ mode === 'login' ? t('login.terms.loginPrefix') : t('login.terms.registerPrefix')}}
          {{ t('login.terms.suffix') }}
          <a href="javascript:;" class="text-white/80 mx-1" @click="openAgreementPage('terms')"> {{ t('login.terms.userAgreement') }} </a>
          {{ t('login.terms.and') }}
          <a href="javascript:;" class="text-white/80 mx-1" @click="openAgreementPage('privacy')"> {{ t('login.terms.privacyPolicy') }} </a>
        </p>
      </div>
    </div>
  </div>
</template>